import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem, // Import from Shadcn/UI, not @radix-ui/react-select
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/src/components/ui/select';
import { ErrorMessage } from '@hookform/error-message';
import { useLocale } from 'next-intl';
import { FC } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

type SelectProps = {
  name: string;
  defaultValue?: string;
  placeholder?: string;
  label?: string;
  options: Array<Record<string, any>>;
  refOptionId: string;
  refOptionName: string;
  required?: boolean;
  disabled?: boolean;
};

const SelectInput: FC<SelectProps> = ({
  name,
  label,
  defaultValue = '',
  placeholder,
  options,
  required = false,
  refOptionId,
  refOptionName,
  disabled,
}) => {
  const locale = useLocale();
  const isAr = locale === 'ar';
  const {
    control,
    formState: { errors },
  } = useFormContext();

  return (
    <div className="my-2 w-full">
      {label && (
        <label className="text-gray-250 mb-2 flex items-center gap-1 font-bold" htmlFor={name}>
          {label}
          {required && <span className="">*</span>}
        </label>
      )}

      <Controller
        control={control}
        name={name}
        defaultValue={defaultValue}
        render={({ field: { value, onChange } }) => (
          <Select
            onValueChange={(val) => onChange(val)} // Ensure onChange updates form state
            value={value ? String(value) : ''} // Convert value to string
            defaultValue={defaultValue}
            dir={isAr ? 'rtl' : 'ltr'}
            disabled={disabled}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder={placeholder} />
            </SelectTrigger>

            <SelectContent>
              <SelectGroup>
                {placeholder && <SelectLabel>{placeholder}</SelectLabel>}
                {options.map((option) => (
                  <SelectItem
                    key={option[refOptionId]}
                    value={String(option[refOptionId])} // Ensure value is a string
                  >
                    {option[refOptionName]}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        )}
      />
      <ErrorMessage
        errors={errors}
        name={name}
        render={({ message }) => <p className="mt-1 text-sm text-red-500">{message}</p>}
      />
    </div>
  );
};

export default SelectInput;
